import React, { useMemo, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BOTTOM_DROP_ZONE_ID = "bottom-drop-zone";

function SortableTicketItem({ id, children, setItemNode, lockedSize = null }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const resolvedWidth =
    lockedSize?.width != null
      ? `${Math.round(lockedSize.width)}px`
      : "var(--kds-board-column-width, 200px)";
  const resolvedHeight =
    lockedSize?.height != null ? `${Math.round(lockedSize.height)}px` : undefined;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.18 : 1,
    zIndex: isDragging ? 2 : 1,
    width: resolvedWidth,
    minWidth: resolvedWidth,
    maxWidth: resolvedWidth,
    flex: `0 0 ${resolvedWidth}`,
    height: resolvedHeight,
    minHeight: resolvedHeight,
    maxHeight: resolvedHeight,
  };

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        setItemNode?.(id, node);
      }}
      style={style}
      className="touch-none shrink-0 grow-0"
    >
      {typeof children === "function"
        ? children({
            dragHandleProps: {
              ...attributes,
              ...listeners,
              ref: setActivatorNodeRef,
            },
            isDragging,
          })
        : children}
    </div>
  );
}

function BottomDropZone() {
  const { setNodeRef, isOver } = useDroppable({
    id: BOTTOM_DROP_ZONE_ID,
  });

  return (
    <div ref={setNodeRef} className="fixed w-full h-1/4 bottom-0 left-0 right-0 z-[70]">
      <div className="flex items-center justify-center py-2">
        <span className="text-gray-700 dark:text-gray-300 font-semibold bg-white/80 dark:bg-gray-900/70 px-3 py-1 rounded-full shadow">
          Drop to Choose Page
        </span>
      </div>

      <div
        id={BOTTOM_DROP_ZONE_ID}
        className={`flex items-center justify-center border-t-2 border-dashed w-full h-full transition-colors ${
          isOver
            ? "bg-yellow-200/70 dark:bg-yellow-700/40 border-yellow-500 dark:border-yellow-300"
            : "bg-gray-200/60 dark:bg-gray-700/60 border-gray-400 dark:border-gray-500"
        }`}
      />
    </div>
  );
}

export default function DndTicketBoard({
  cards,
  onDragStateChange,
  onReorder,
  onOpenPagePicker,
  renderCard,
  boardClassName = "",
  boardStyle,
}) {
  const [activeId, setActiveId] = useState(null);
  const [activeSize, setActiveSize] = useState(null);
  const [lockedSizes, setLockedSizes] = useState(null);
  const itemNodesRef = useRef(new Map());

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 70,
        tolerance: 8,
      },
    })
  );

  const cardIds = useMemo(() => cards.map((card) => card.id), [cards]);
  const activeCard = useMemo(
    () => cards.find((card) => card.id === activeId) || null,
    [activeId, cards]
  );

  const setItemNode = (id, node) => {
    if (node) {
      itemNodesRef.current.set(id, node);
    } else {
      itemNodesRef.current.delete(id);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => {
        const nextLockedSizes = {};
        itemNodesRef.current.forEach((node, id) => {
          const rect = node?.getBoundingClientRect?.();
          if (!rect) return;
          nextLockedSizes[id] = {
            width: rect.width,
            height: rect.height,
          };
        });
        const activeNode = itemNodesRef.current.get(active.id);
        const activeRect = activeNode?.getBoundingClientRect?.();
        setLockedSizes(nextLockedSizes);
        setActiveSize(
          activeRect
            ? {
                width: activeRect.width,
                height: activeRect.height,
              }
            : null
        );
        setActiveId(active.id);
        onDragStateChange?.(true);
      }}
      onDragCancel={() => {
        setActiveId(null);
        setActiveSize(null);
        setLockedSizes(null);
        onDragStateChange?.(false);
      }}
      onDragEnd={({ active, over }) => {
        const activeKey = String(active?.id || "");
        const overKey = over?.id != null ? String(over.id) : null;

        if (activeKey && overKey === BOTTOM_DROP_ZONE_ID) {
          onOpenPagePicker?.(activeKey);
        } else if (activeKey && overKey && activeKey !== overKey) {
          const oldIndex = cardIds.indexOf(activeKey);
          const newIndex = cardIds.indexOf(overKey);
          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            const nextPageKeys = arrayMove(cardIds, oldIndex, newIndex);
            onReorder?.({
              nextPageKeys,
              changedKey: activeKey,
            });
          }
        }

        setActiveId(null);
        setActiveSize(null);
        setLockedSizes(null);
        onDragStateChange?.(false);
      }}
    >
      <SortableContext items={cardIds} strategy={horizontalListSortingStrategy}>
        <div className={boardClassName} style={boardStyle}>
          {cards.map((card) => (
            <SortableTicketItem
              key={card.id}
              id={card.id}
              setItemNode={setItemNode}
              lockedSize={
                activeId
                  ? lockedSizes?.[card.id] ||
                    (activeId === card.id ? activeSize : null) ||
                    null
                  : null
              }
            >
              {({ dragHandleProps, isDragging }) =>
                renderCard(card, { isDragging: activeId === card.id || isDragging, dragHandleProps })
              }
            </SortableTicketItem>
          ))}
        </div>
      </SortableContext>

      {activeId ? <BottomDropZone /> : null}

      <DragOverlay>
        {activeCard ? (
          <div
            className="pointer-events-none"
            style={{
              width: activeSize?.width ? `${Math.round(activeSize.width)}px` : undefined,
              minWidth: activeSize?.width ? `${Math.round(activeSize.width)}px` : undefined,
              maxWidth: activeSize?.width ? `${Math.round(activeSize.width)}px` : undefined,
            }}
          >
            {renderCard(activeCard, { isOverlay: true })}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
